<template>
  <VaDropdown :offset="[13, 0]" class="notification-dropdown" stick-to-edges :close-on-content-click="false">
    <template #anchor>
      <VaButton preset="secondary" color="textPrimary">
        <VaBadge overlap>
          <template #text>{{ unreadCount > 99 ? '99+' : unreadCount }}</template>
          <VaIconNotification class="notification-dropdown__icon" />
        </VaBadge>
      </VaButton>
    </template>

    <VaDropdownContent class="h-full sm:max-w-[620px] sm:h-auto">
      <section class="sm:max-h-[320px] p-4 overflow-auto">
        <VaList class="space-y-1 mb-2">
          <template v-for="(item, index) in notificationsWithRelativeTime" :key="item.id">
            <RouterLink
              v-if="item.route"
              :to="item.route"
              class="block text-inherit no-underline"
              :class="{ 'opacity-60': item.read }"
              @click="onNotificationClick(item.id)"
            >
              <VaListItem class="text-base hover:bg-primary-50 rounded-lg">
                <VaListItemSection icon class="mx-0 p-0">
                  <VaIcon :name="item.icon" color="secondary" />
                </VaListItemSection>
                <VaListItemSection>
                  {{ item.message }}
                </VaListItemSection>
                <VaListItemSection icon class="mx-1">
                  {{ item.updateTimestamp }}
                </VaListItemSection>
              </VaListItem>
            </RouterLink>

            <!-- 🔘 Otherwise just display -->
            <VaListItem v-else class="text-base hover:bg-primary-50 rounded-lg" @click="onNotificationClick(item.id)">
              <VaListItemSection icon class="mx-0 p-0">
                <VaIcon :name="item.icon" color="secondary" />
              </VaListItemSection>
              <VaListItemSection>
                {{ item.message }}
              </VaListItemSection>
              <VaListItemSection icon class="mx-1">
                {{ item.updateTimestamp }}
              </VaListItemSection>
            </VaListItem>

            <VaListSeparator v-if="item.separator && index !== notificationsWithRelativeTime.length - 1" class="mx-3" />
          </template>
        </VaList>

        <VaButton
          v-if="notificationsWithRelativeTime && notificationsWithRelativeTime.length > 4"
          preset="primary"
          class="w-full"
          @click="toggleDisplayAll"
        >
          {{ displayAll ? t('notifications.less') : t('notifications.all') }}
        </VaButton>
      </section>
    </VaDropdownContent>
  </VaDropdown>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { RouterLink } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useNotificationStore } from '../../../../stores/notificationStore'

const { t, locale } = useI18n()
const store = useNotificationStore()

const rtf = new Intl.RelativeTimeFormat(locale.value, { style: 'short' })

const TIME_NAMES = {
  second: 1000,
  minute: 1000 * 60,
  hour: 1000 * 60 * 60,
  day: 1000 * 60 * 60 * 24,
  week: 1000 * 60 * 60 * 24 * 7,
  month: 1000 * 60 * 60 * 24 * 30,
  year: 1000 * 60 * 60 * 24 * 365,
}

const getTimeName = (diff: number) => {
  return Object.keys(TIME_NAMES).reduce(
    (acc, key) => (TIME_NAMES[key as keyof typeof TIME_NAMES] < diff ? key : acc),
    'month',
  ) as keyof typeof TIME_NAMES
}

const notificationsWithRelativeTime = computed(() => {
  const list = store.visibleNotifications
  return list.map((item, index) => {
    const diff = Date.now() - new Date(item.updateTimestamp).getTime()
    const timeName = getTimeName(diff)
    const nextItem = list[index + 1]
    const nextDiff = nextItem ? Date.now() - new Date(nextItem.updateTimestamp).getTime() : null
    const nextName = nextDiff ? getTimeName(nextDiff) : null
    const separator = nextName && timeName !== nextName

    return {
      ...item,
      updateTimestamp: rtf.format(-1 * Math.round(diff / TIME_NAMES[timeName]), timeName),
      separator,
    }
  })
})

const toggleDisplayAll = () => store.toggleDisplayAll()
const displayAll = computed(() => store.displayAll)
const unreadCount = computed(() => store.unreadCount)

const onNotificationClick = (id: string) => {
  store.markAsRead(id)
}
</script>
